// domain/constants/programStatus.js

/**
 * Where a program sits in its own lifecycle.
 *
 * Unlike a target's status, this one is a single axis: every value is a clinician's decision, and
 * nothing in data collection advances it.
 *
 * @domainObject value-object
 * @invariant Never defaulted on an overwrite: re-importing a file with no status column would
 *   silently reactivate closed programs. Only a program being created may be given a default.
 * @typedef {'waiting'|'baseline'|'active'|'maintenance'|'hold'|'closed'|'discontinued'} ProgramStatus
 */
export const PROGRAM_STATUS = {
  WAITING: 'waiting',
  BASELINE: 'baseline',
  ACTIVE: 'active',
  MAINTENANCE: 'maintenance',
  HOLD: 'hold',
  CLOSED: 'closed',
  DISCONTINUED: 'discontinued',
};

/**
 * Label keys and display order. See the note in `targetStatus` on why these are keys rather than
 * resolved strings, and why icon and colour are not here.
 */
export const PROGRAM_STATUS_META = {
  [PROGRAM_STATUS.WAITING]: { value: PROGRAM_STATUS.WAITING, labelKey: 'programStatus_waiting', order: 10 },
  [PROGRAM_STATUS.BASELINE]: { value: PROGRAM_STATUS.BASELINE, labelKey: 'programStatus_baseline', order: 20 },
  [PROGRAM_STATUS.ACTIVE]: { value: PROGRAM_STATUS.ACTIVE, labelKey: 'programStatus_active', order: 30 },
  [PROGRAM_STATUS.MAINTENANCE]: { value: PROGRAM_STATUS.MAINTENANCE, labelKey: 'programStatus_maintenance', order: 40 },
  [PROGRAM_STATUS.HOLD]: { value: PROGRAM_STATUS.HOLD, labelKey: 'programStatus_hold', order: 50 },
  [PROGRAM_STATUS.CLOSED]: { value: PROGRAM_STATUS.CLOSED, labelKey: 'programStatus_closed', order: 60 },
  [PROGRAM_STATUS.DISCONTINUED]: {
    value: PROGRAM_STATUS.DISCONTINUED,
    labelKey: 'programStatus_discontinued',
    order: 70,
  },
};

/**
 * @param {unknown} value
 * @returns {boolean}
 */
export function isProgramStatus(value) {
  return Object.prototype.hasOwnProperty.call(PROGRAM_STATUS_META, value);
} // isProgramStatus()
